// OP-3605 — A mesma lista de pedidos em dois modos (ESL1): a FILA mostra só o
// que ainda pede trabalho (`abertos=1`) e por isso consegue ficar vazia; o
// HISTÓRICO é a tela de consulta — todos os pedidos, inclusive os que já
// fecharam, com o despachado por linha. A decisão é na tela do pedido.
// OP-3619 — no celular a tabela vira cartão e os filtros viram chips + folha.
import { FC, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useEstoqueLocais, useEstoqueSolicitacoes } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { urlEstoque } from '../../../../Service/estoque'
import { FilaSolicitacaoRow, SolicitacoesFilters, StatusSolicitacao, TIPO_DEMANDA_LABEL, TipoDemanda, rotuloTipoLocal } from '../../../../types/estoque'
import {
  BarraChips, BD, Button, CaixaLista, Card, Chip, Field, Input, ListaCartoes, PageSubtitle, PageTitle,
  Select, SoDesktop, Spinner, StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { Cartao } from '../Cartao'
import { FabLink } from '../FabLink'
import { Filtros } from '../Filtros'
import { statusDaUrl } from '../filtrosUrl'
import { plural, prazoUi, progressoRegime } from '../formato'
import { STATUS_FINAIS, STATUS_SOLICITACAO_UI } from '../statusSolicitacao'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

export const ABERTOS: StatusSolicitacao[] = ['EM_ANALISE', 'AGUARDANDO_INFO', 'APROVADA', 'EM_SEPARACAO', 'EM_ESPERA', 'PRONTA', 'ATENDIDA_PARCIAL']
const TODOS: StatusSolicitacao[] = [...ABERTOS, 'EXPEDIDA', ...STATUS_FINAIS]
const TIPOS: TipoDemanda[] = ['INSTALACAO', 'ENVIO', 'PORTARIA', 'COLETA']
const TITULO_SEPARADO = 'Separado sobre o pedido, por regime: peças (com série) e unidades de consumível. Os dois não se somam.'
const TITULO_DESPACHADO = 'Já despachado sobre o pedido, por regime: peças (com série) e unidades de consumível. Acumula na expedição; não é o que o detentor recebeu.'

export type ModoLista = 'fila' | 'historico'

const MODO: Record<ModoLista, { titulo: string; subtitulo: string; filtersKey: string; status: StatusSolicitacao[]; vazio: string; todos: string }> = {
  fila: {
    titulo: 'Fila da logística',
    subtitulo: 'O que está em aberto agora. Pedido despachado sai daqui: o trabalho passa a ser da remessa.',
    filtersKey: 'estoque.fila.filtros',
    status: ABERTOS,
    vazio: 'Fila vazia com os filtros atuais.',
    todos: 'Em aberto',
  },
  historico: {
    titulo: 'Histórico de solicitações',
    subtitulo: 'Todos os pedidos, incluindo os que já fecharam. É a tela de consulta — a Fila mostra só o que ainda pede trabalho.',
    filtersKey: 'estoque.historico.filtros',
    status: TODOS,
    vazio: 'Nada com esses filtros.',
    todos: 'Todos',
  },
}

const loadFilters = (key: string): SolicitacoesFilters | null => {
  try { const raw = sessionStorage.getItem(key); return raw ? JSON.parse(raw) : null } catch { return null }
}

/** Sem status escolhido, a Fila pede só os abertos; o Histórico pede tudo. */
export const consultaDe = (modo: ModoLista, f: SolicitacoesFilters): SolicitacoesFilters =>
  modo === 'fila' && !f.status ? { ...f, abertos: '1' } : f

/**
 * Quantos filtros da FOLHA estão valendo — é o contador do botão no celular.
 * O status não entra: lá ele é chip, e um badge apontando para uma folha que
 * não o mostra faria a pessoa procurar o que já está na tela.
 */
export const filtrosAtivos = (f: SolicitacoesFilters): number =>
  [f.busca, f.tipoDemanda, f.localId].filter((v) => v != null && String(v) !== '').length

export const ListaSolicitacoes: FC<{ modo: ModoLista }> = ({ modo }) => {
  const cfg = MODO[modo]
  const navigate = useNavigate()
  const { pode } = usePerfilEstoque()
  const podeSolicitar = pode('SOLICITAR')
  const [searchParams] = useSearchParams()
  const statusUrl = statusDaUrl(searchParams, cfg.status)
  const inicial = useMemo<SolicitacoesFilters>(
    () => (statusUrl ? { page: 1, status: statusUrl } : loadFilters(cfg.filtersKey) ?? { page: 1 }),
    [cfg.filtersKey, statusUrl],
  )
  const [draft, setDraft] = useState<SolicitacoesFilters>(inicial)
  const [applied, setApplied] = useState<SolicitacoesFilters>(inicial)
  const { rows, total, page, lastPage, isLoading, isError, isUnauthorized, mutate } = useEstoqueSolicitacoes(consultaDe(modo, applied))
  const { locais } = useEstoqueLocais()
  const COLS = modo === 'historico' ? 9 : 8

  useEffect(() => {
    if (!statusUrl) return
    setDraft((d) => ({ ...d, status: statusUrl, page: 1 }))
    setApplied((a) => ({ ...a, status: statusUrl, page: 1 }))
  }, [statusUrl])
  useEffect(() => { try { sessionStorage.setItem(cfg.filtersKey, JSON.stringify(applied)) } catch { /* ignora */ } }, [applied, cfg.filtersKey])

  const aplicar = () => {
    const proximo = { ...draft, page: 1 }
    setApplied(proximo)
    if (urlEstoque('getEstoqueSolicitacoes', consultaDe(modo, proximo)) === urlEstoque('getEstoqueSolicitacoes', consultaDe(modo, applied))) mutate()
  }
  const limpar = () => {
    setDraft({ page: 1 })
    setApplied({ page: 1 })
  }
  const escolherStatus = (status: StatusSolicitacao | '') => {
    setDraft((d) => ({ ...d, status, page: 1 }))
    setApplied((a) => ({ ...a, status, page: 1 }))
  }
  const irPagina = (p: number) => setApplied((a) => ({ ...a, page: p }))
  const ordenar = (col: string) => {
    const dir = applied.sortBy === col && applied.sortDir !== 'desc' ? 'desc' : 'asc'
    setDraft((d) => ({ ...d, sortBy: col, sortDir: dir }))
    setApplied((a) => ({ ...a, sortBy: col, sortDir: dir, page: 1 }))
  }
  const seta = (col: string) => (applied.sortBy === col ? (applied.sortDir === 'desc' ? ' ▼' : ' ▲') : '')

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const contagem = isLoading ? 'Carregando…' : plural(total, 'pedido', 'pedidos')

  const eventosDe = (r: FilaSolicitacaoRow): string => {
    if (r.eventoNomes) return r.eventoNomes
    return r.eventos > 0 ? `${r.eventos} evento${r.eventos === 1 ? '' : 's'}` : 'sem evento'
  }

  const linha = (r: FilaSolicitacaoRow) => {
    const ui = STATUS_SOLICITACAO_UI[r.status] ?? { bg: '#F3F4F6', color: '#374151', label: r.status }
    const separado = progressoRegime({ pecasFeitas: r.pecasSeparadas, pecasPedidas: r.pecasPedidas, consumoFeito: r.consumoSeparado, consumoPedido: r.consumoPedido })
    const despachado = progressoRegime({ pecasFeitas: r.pecasAtendidas, pecasPedidas: r.pecasPedidas, consumoFeito: r.consumoAtendido, consumoPedido: r.consumoPedido })
    return (
      <tr key={r.id} onClick={() => navigate(`/interno/estoque/fila/${r.id}`)} style={{ cursor: 'pointer' }}>
        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.numero}</td>
        <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDateTime(r.criadoEm)}</td>
        <td>{TIPO_DEMANDA_LABEL[r.tipoDemanda] ?? r.tipoDemanda}</td>
        <td><b>{r.detentorNome ?? '—'}</b>{r.detentorTipo && <div style={{ fontSize: 11, color: BD.grayText }}>{rotuloTipoLocal(r.detentorTipo)}</div>}</td>
        <td>
          {r.solicitanteNome ?? '—'}
          {/* O evento fica junto de quem pediu: é o par que a logística usa
              para achar o pedido quando alguém liga perguntando. */}
          <div style={{ fontSize: 12, color: BD.grayText }}>{eventosDe(r)}</div>
        </td>
        <td>{r.pracaNome}</td>
        <td>{fmtDate(r.prazoEm)}</td>
        <td>
          <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge>
          {modo === 'fila' && <div style={{ fontSize: 12, color: BD.grayText }} title={TITULO_SEPARADO}>separado: {separado}</div>}
        </td>
        {modo === 'historico' && (
          // Acumula na EXPEDIÇÃO: é o que saiu, não o que o detentor recebeu —
          // por isso a coluna se chama "Despachado".
          <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: 13 }} title={TITULO_DESPACHADO}>{despachado}</td>
        )}
      </tr>
    )
  }

  const cartao = (r: FilaSolicitacaoRow) => {
    const ui = STATUS_SOLICITACAO_UI[r.status] ?? { bg: '#F3F4F6', color: '#374151', label: r.status }
    const separado = progressoRegime({ pecasFeitas: r.pecasSeparadas, pecasPedidas: r.pecasPedidas, consumoFeito: r.consumoSeparado, consumoPedido: r.consumoPedido })
    const despachado = progressoRegime({ pecasFeitas: r.pecasAtendidas, pecasPedidas: r.pecasPedidas, consumoFeito: r.consumoAtendido, consumoPedido: r.consumoPedido })
    const prazo = prazoUi(r.prazoEm)
    const encerrado = STATUS_FINAIS.indexOf(r.status) >= 0
    return (
      <Cartao
        key={r.id}
        to={`/interno/estoque/fila/${r.id}`}
        codigo={r.numero}
        badge={ui}
        titulo={r.detentorNome ?? '—'}
        esmaecido={encerrado}
        destaque={!encerrado && prazo.alerta}
        meta={(
          <>
            {TIPO_DEMANDA_LABEL[r.tipoDemanda] ?? r.tipoDemanda} · {eventosDe(r)}
            <div>{r.pracaNome} · pediu {r.solicitanteNome ?? '—'}</div>
          </>
        )}
        rodape={(
          <span style={{ color: !encerrado && prazo.alerta ? BD.redDark : BD.grayText, fontWeight: !encerrado && prazo.alerta ? 600 : 400 }}>
            {encerrado ? fmtDate(r.prazoEm) : prazo.texto}
          </span>
        )}
        rodapeFim={modo === 'fila' ? `separado: ${separado}` : `despachado: ${despachado}`}
      />
    )
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>{cfg.titulo}</PageTitle>
          <PageSubtitle>{cfg.subtitulo}</PageSubtitle>
        </div>
        {podeSolicitar && (
          <SoDesktop>
            <Button onClick={() => navigate('/interno/estoque/solicitar')}>Nova solicitação</Button>
          </SoDesktop>
        )}
      </div>

      <BarraChips role="group" aria-label="Status">
        <Chip type="button" $ativo={!applied.status} onClick={() => escolherStatus('')}>{cfg.todos}</Chip>
        {cfg.status.map((s) => (
          <Chip key={s} type="button" $ativo={applied.status === s} onClick={() => escolherStatus(s)}>
            {STATUS_SOLICITACAO_UI[s].label}
          </Chip>
        ))}
      </BarraChips>

      <Filtros
        aoAplicar={aplicar}
        aoLimpar={limpar}
        ativos={filtrosAtivos(applied)}
        resumo={contagem}
        confirmar="Aplicar filtros"
      >
        <Field>
          <label>Número, produtor ou solicitante</label>
          <Input value={draft.busca ?? ''} onChange={(e) => setDraft((d) => ({ ...d, busca: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && aplicar()} />
        </Field>
        <Field>
          <label>Status</label>
          <Select value={draft.status ?? ''} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as StatusSolicitacao | '' }))}>
            <option value="">{modo === 'fila' ? 'todos em aberto' : 'todos'}</option>
            {cfg.status.map((s) => <option key={s} value={s}>{STATUS_SOLICITACAO_UI[s].label}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Tipo</label>
          <Select value={draft.tipoDemanda ?? ''} onChange={(e) => setDraft((d) => ({ ...d, tipoDemanda: e.target.value as TipoDemanda | '' }))}>
            <option value="">todos</option>
            {TIPOS.map((t) => <option key={t} value={t}>{TIPO_DEMANDA_LABEL[t]}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Praça</label>
          <Select value={draft.localId ?? ''} onChange={(e) => setDraft((d) => ({ ...d, localId: e.target.value }))}>
            <option value="">todas</option>
            {locais.filter((l) => l.noEscopo).map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </Select>
        </Field>
      </Filtros>

      <CaixaLista>
        <SoDesktop>
          <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>{contagem}</div>
        </SoDesktop>
        <TabelaWrap>
          <Table>
            <thead>
              <tr>
                <th onClick={() => ordenar('numero')} style={{ cursor: 'pointer' }}>Número{seta('numero')}</th>
                <th onClick={() => ordenar('criado')} style={{ cursor: 'pointer' }}>Aberta em{seta('criado')}</th>
                <th>Tipo</th><th>Para quem</th><th>Solicitante / evento</th><th>Praça</th>
                <th onClick={() => ordenar('prazo')} style={{ cursor: 'pointer' }}>Prazo{seta('prazo')}</th>
                <th onClick={() => ordenar('status')} style={{ cursor: 'pointer' }}>Status{seta('status')}</th>
                {modo === 'historico' && <th style={{ textAlign: 'right' }} title={TITULO_DESPACHADO}>Despachado</th>}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>Não foi possível carregar os pedidos.</td></tr>}
              {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>{cfg.vazio}</td></tr>}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && <Card style={{ padding: 20, color: BD.red }}>Não foi possível carregar os pedidos.</Card>}
          {!isLoading && !isError && rows.length === 0 && <Card style={{ padding: 20, color: BD.grayText }}>{cfg.vazio}</Card>}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={(page ?? 1) <= 1} onClick={() => irPagina((page ?? 1) - 1)}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {page ?? 1}</span>
          <Button $variant="secondary" disabled={!!lastPage} onClick={() => irPagina((page ?? 1) + 1)}>Próxima ›</Button>
        </div>
      </CaixaLista>

      {podeSolicitar && <FabLink to="/interno/estoque/solicitar" rotulo="Nova solicitação" />}
    </>
  )
}
